import { useCallback, useMemo, useRef, useState } from 'react';
import {
  defaultColors as colors,
  Menu,
  MenuTypes,
  TextField,
} from '@zeptonow/zui';
import { To, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from './common/component/error-boundary';
import useHasPermission from './common/hooks/useHasPermission';
import getModuleRoutes from './common/routes/ModuleRoutes';
import { FilterRoutesInterface, SideBarPropsInterface } from './types';
import {
  InputAdornment,
  inputBaseClasses,
  outlinedInputClasses,
} from '@mui/material';
import { ModuleRoutesInterface } from './common/routes/types';
import { generateImage } from '@hrms/common/utils/generateImage';

const filterRoutesOnPermission: FilterRoutesInterface = (
  routes,
  hasModulePermission,
) => {
  if (!routes || !routes.length) return undefined;
  return routes
    .filter(
      (route) =>
        route.sidebarVisibility !== false &&
        hasModulePermission({ moduleId: route.id }),
    )
    .map((route) => ({
      ...route,
      children: filterRoutesOnPermission(route.children, hasModulePermission),
    }));
};

const searchedRoutes = (
  searchQuery: string,
  filteredRoutes: Array<ModuleRoutesInterface>,
) => {
  if (!searchQuery) return filteredRoutes;
  const searchFilter = (
    routes: Array<ModuleRoutesInterface>,
  ): Array<ModuleRoutesInterface> => {
    return routes
      .map((route) => {
        const matchedChildren = route.children
          ? searchFilter(route.children as Array<ModuleRoutesInterface>)
          : [];
        if (
          route.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          matchedChildren.length > 0
        ) {
          return {
            ...route,
            children: matchedChildren,
          };
        }

        return null;
      })
      .filter((route) => route !== null) as Array<ModuleRoutesInterface>;
  };

  return searchFilter(filteredRoutes);
};

export const SideBar = (props: SideBarPropsInterface) => {
  const { sideBarOpen } = props;
  const { hasModulePermission } = useHasPermission();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const moduleRoutes = useMemo(() => getModuleRoutes(), []);

  const filteredRoutes = useMemo(() => {
    const searchROutes = searchedRoutes(searchQuery, moduleRoutes);
    return filterRoutesOnPermission(searchROutes, hasModulePermission) ?? [];
  }, [moduleRoutes, hasModulePermission]);

  const onMenuItemClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, route: To) => {
      navigate(route);
      //   pushEventToMixpanel({ event: EventName.ROUTE_CLICKED, data: { route } });
    },
    [navigate],
  );

  const onChangeSearchBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const onClickSearchBox = () => {
    inputRef?.current?.focus();
  };

  const onClickCancelButton = () => {
    if (!sideBarOpen) inputRef?.current?.focus();
  };

  return (
    <div className="my-4 bg-gray-900">
      <ErrorBoundary>
        <div className="p-4">
          {sideBarOpen ? (
            <TextField
              errorMessage=""
              variant="outlined"
              sx={{
                [`& .${inputBaseClasses.root}`]: {
                  borderRadius: '0.5rem !important',
                  backgroundColor: '#1D2939 !important',
                },
                [`& .${outlinedInputClasses.input}`]: {
                  color: colors.gray[50],
                  width: !sideBarOpen ? 0 : '100%',
                },
              }}
              ref={inputRef}
              onClick={onClickSearchBox}
              adornment={{
                startAdornment: (
                  <InputAdornment position="start">
                    {generateImage({
                      name: 'search-white',
                      alt: 'search icon',
                      className: 'h-3.5 w-3.5',
                    })}
                  </InputAdornment>
                ),
                ...(searchQuery.length > 0 &&
                  sideBarOpen && {
                    endAdornment: (
                      <InputAdornment position="end">
                        <div
                          className="cursor-pointer"
                          onClick={() => setSearchQuery('')}
                        >
                          {generateImage({
                            name: 'cross',
                            alt: 'cross icon',
                            className: 'h-2.5 w-2.5',
                          })}
                        </div>
                      </InputAdornment>
                    ),
                  }),
              }}
              size="small"
              className="rounded-lg border-none bg-gray-800"
              placeholder="Search"
              value={searchQuery}
              onChange={onChangeSearchBox}
            />
          ) : (
            <div
              className="bg-gray-800 flex flex-row justify-center items-center h-10 rounded-lg cursor-pointer"
              onClick={onClickCancelButton}
            >
              {generateImage({
                name: 'search-white',
                alt: 'search icon',
                className: 'h-3.5 w-3.5',
              })}
            </div>
          )}
        </div>
        <Menu
          items={filteredRoutes as Array<MenuTypes>}
          sidebarOpen={sideBarOpen}
          onClick={onMenuItemClick}
          currentPath={pathname}
        />
      </ErrorBoundary>
    </div>
  );
};

export default SideBar;
